import type { AddRequestFormData } from './validationScheme';
import { useEffect, useState } from 'react';
import type { SubmitErrorHandler, SubmitHandler } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Modal, TextField, Tooltip, useTheme } from '@mui/material';
import { observer } from 'mobx-react';
import { useStore } from '@stores/RootStore';
import { BulletIcon, ErrorIcon, XIcon } from '@icons/index';
import { useAddTenantMembers, useGetTenantMembers } from '@queryHooks/useTenant';

import type { Page } from '@type/Page';
import type { TenantMemberSimpleModel } from '@repositories/tenantMemberRepository/models/TenantMemberSimpleModel';
import { getErrorMessage } from '@locales/i18nErrorMessage';
import * as S from './ExternalManagerAddFromModalStyle';
import { useAddRequestForm } from './validationScheme';

interface Props {
  isClose: () => void;
  isOpen: boolean;
  adminUsername: string[];
}

export const ExternalManagerAddFromModal = observer(({ isOpen, isClose, adminUsername }: Props) => {
  const { t } = useTranslation();
  const {
    uiStore: { toastStore },
  } = useStore();
  const theme = useTheme();
  const [isNextInfo, setIsNextInfo] = useState(false);
  const [isManager, setIsManager] = useState(false);
  const {
    handleSubmit,
    formState: { errors },
    getValues,
    watch,
    reset,
    control,
    setError,
  } = useAddRequestForm();
  const account = getValues('email');

  const { authStore } = useStore();
  useGetTenantMembers(
    authStore.curTenant.id,
    { keyword: account },
    {
      enabled: !!account,
      onSuccess: (data: Page<TenantMemberSimpleModel>) => {
        if (!data.empty && data.content.find((member: TenantMemberSimpleModel) => member.email === account)) {
          setError('email', { message: t('Manager_Config.Already_Joined_Error') });
        }
      },
      cacheTime: 0,
      staleTime: 0,
    },
  );
  const { mutate: addTenantMember, isPending } = useAddTenantMembers();

  const handleForm: {
    valid: SubmitHandler<AddRequestFormData>;
    invalid: SubmitErrorHandler<AddRequestFormData>;
  } = {
    valid: (/* data */) => {
      // console.debug('Valid', data);
    },
    invalid: (/* data */) => {
      // console.debug('Invalid', data);
    },
  };

  const handleClickNextBtn = () => {
    const checkUsername = adminUsername.find(username => username === account);
    if (checkUsername !== account) {
      setIsNextInfo(true);
    } else {
      setIsManager(true);
    }
  };

  const handleClickAdminAddBtn = () => {
    addTenantMember(
      [
        {
          memberRole: 'EXTERNAL_ADMIN',
          tenantId: authStore.managerTenantInfo.id,
          username: account,
        },
      ],
      {
        onSuccess: isClose,
        onError: error => {
          const errorMessage = getErrorMessage(error);
          if (errorMessage.errorType === 'TENANT.ERROR.ALREADY_EXIST_MEMBER') {
            toastStore?.open?.(t('Error.Already_Exist_Manager'));
          } else {
            toastStore?.open?.(t('Error.Manager_Create_Failed'));
          }
        },
      },
    );
  };

  useEffect(() => {
    if (!isOpen) {
      setIsNextInfo(false);
      reset();
    }
  }, [isOpen, reset]);

  return (
    <Modal open={isOpen}>
      <S.ModalWrapper onSubmit={handleSubmit(handleForm.valid)}>
        <S.Header>
          <S.HeaderTitle>{t('Member_Manager_05')}</S.HeaderTitle>
          <S.IconButton onClick={isClose}>
            <XIcon color={theme.colors['ic-gray-main']} width={24} height={24} />
          </S.IconButton>
        </S.Header>
        {!isNextInfo ? (
          <S.Body>
            <S.subTitle>
              {t('Member_User_14')}
              <span>*</span>
            </S.subTitle>
            <Controller
              name="email"
              control={control}
              rules={{ required: t('Account_Validation_Check_02') }}
              render={({ field: { onChange, value } }) => (
                <TextField
                  value={value}
                  onChange={onChange}
                  size="medium"
                  fullWidth
                  error={!!errors.email || isManager}
                  helperText={isManager ? t('Member_Manager_16') : errors.email?.message}
                  placeholder="example@gamil.com"
                />
              )}
            />

            <S.InfoWrapper>
              <S.InfoItem>
                <BulletIcon width={6} height={6} color={theme.colors['ic-gray-lighter']} />
                <span>{t('Member_Manager_09')}</span>
              </S.InfoItem>
              <S.InfoItem>
                <BulletIcon width={6} height={6} color={theme.colors['ic-gray-lighter']} />
                <span>{t('Member_Manager_10')}</span>
              </S.InfoItem>
            </S.InfoWrapper>
          </S.Body>
        ) : (
          <S.Body $isNextInfoModal={isNextInfo}>
            <S.IconWrapper>
              <ErrorIcon width={32} height={32} color={theme.colors['ic-primary-light']} />
            </S.IconWrapper>
            <p>{t('Member_Manager_13')}</p>
            <Tooltip title={account} arrow placement="bottom-start">
              <S.EmailWrapper>
                <span>{account}</span>
              </S.EmailWrapper>
            </Tooltip>
          </S.Body>
        )}
        <S.Footer>
          <S.StyledSecureButton
            isPending={isPending}
            disabled={!!errors.email || watch().email === ''}
            variant="contain"
            size="extraLarge"
            type={isNextInfo ? 'submit' : 'button'}
            onClick={isNextInfo ? handleClickAdminAddBtn : handleClickNextBtn}
          >
            {t('Member_Manager_11')}
          </S.StyledSecureButton>
          <S.StyledButton variant="outline" size="extraLarge" onClick={isClose}>
            {t('Subscrib_Detail_BuyRequest_13')}
          </S.StyledButton>
        </S.Footer>
      </S.ModalWrapper>
    </Modal>
  );
});
